import { useAtomValue } from 'jotai';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocalStorageState } from 'ahooks';

import { useRouteInfo } from '@/hooks/use-route-info';
import { useDraftParams } from '@/hooks/use-draft-params';

import { Tab } from '@flintmint/web-shared/components/tab';
import { NoteAiContext } from '@/components/note-ai-context';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Tabs } from '@flintmint/web-shared/components/tabs';
import { TabPanel } from '@flintmint/web-shared/components/tab-panel';
import { TabPanels } from '@flintmint/web-shared/components/tab-panels';
import { EditorSidebarHolder } from '@/components/editor-sidebar-holder';

import { externalWorkspaceTabStateAtom } from '@/state';

import styles from './workspace-sidebar.module.scss';

const defaultTab = 'aiContext';

export const WorkspaceSidebar = memo(() => {
  const externalWorkspaceTabState = useAtomValue(externalWorkspaceTabStateAtom);

  const { isCortex, routeKey } = useRouteInfo();

  const { creatingMedicalNote } = useDraftParams();

  const [activeTab = defaultTab, setActiveTab] = useLocalStorageState<string>(`${routeKey}-workspaceSidebarTab`, {
    defaultValue: defaultTab,
  });

  const hideAiContext = isCortex || creatingMedicalNote;
  const normalizedActiveTab = activeTab === defaultTab ? activeTab : defaultTab;

  useEffect(() => {
    if (externalWorkspaceTabState) {
      setActiveTab(externalWorkspaceTabState.tab === defaultTab ? externalWorkspaceTabState.tab : defaultTab);
    }
  }, [externalWorkspaceTabState, setActiveTab]);

  useEffect(() => {
    if (activeTab !== normalizedActiveTab) {
      setActiveTab(defaultTab);
    }
  }, [activeTab, normalizedActiveTab, setActiveTab]);

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const target = document.getElementById('layout-right-sidebar-content');

    if (target) {
      return setPortalTarget(target);
    }

    const observer = new MutationObserver(() => {
      const nextTarget = document.getElementById('layout-right-sidebar-content');

      if (!nextTarget) {
        return;
      }

      setPortalTarget(nextTarget);
      observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  if (!portalTarget) {
    return null;
  }

  if (hideAiContext) {
    return null;
  }

  return createPortal(
    <EditorSidebarHolder className={styles.workspaceSidebar} data-testid="workspace-sidebar">
      <Tabs value={normalizedActiveTab} appearance="elevated" onChange={(tabId) => setActiveTab(tabId)}>
        <Tab
          id="aiContext"
          name="AI Context"
          icon={<Icon size={20} type="ai-brain-01-twotone" multiColor />}
          data-testid="tab-ai-context"
        />
      </Tabs>
      <TabPanels value={normalizedActiveTab} className={styles.panels}>
        <TabPanel id="aiContext">
          <NoteAiContext />
        </TabPanel>
      </TabPanels>
    </EditorSidebarHolder>,
    portalTarget
  );
});
